/**
 * HostIncidentCard - Compact card showing alerts grouped by host
 *
 * Design: one card per host aggregating its alerts. Muted alerts collapse with
 * countdown; acked alerts show acked_by + relative time.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Button, Card, Text, Collapsible, CollapsibleTrigger, CollapsibleContent } from '@agion/ui'
import {
  Server, BellOff, ChevronDown, ChevronRight,
} from 'lucide-react'
import { cn } from '@/core/infra_utils'
import type { AlertSeverity, MuteDuration } from '@/core/infra_types'
import type { HostIncident } from './alert_helpers'
import { sortAlertsBySeverityAndTime, partitionMutedAlerts } from './alert_helpers'
import { SeverityBadge } from './MonitoringPrimitives'
import { AlertRowCompact } from './AlertRowCompact'

interface HostIncidentCardProps {
  incident: HostIncident
  selectedAlertId: string | null
  onSelectAlert: (alertId: string) => void
  onAck: (alertId: string) => void
  onUnack: (alertId: string) => void
  onMute: (alertId: string, duration: MuteDuration) => void
  onUnmute: (alertId: string) => void
  isMutating: boolean
}

export function HostIncidentCard({
  incident,
  selectedAlertId,
  onSelectAlert,
  onAck,
  onUnack,
  onMute,
  onUnmute,
  isMutating,
}: HostIncidentCardProps) {
  const { t } = useTranslation(['pages'])
  const [mutedExpanded, setMutedExpanded] = useState(false)

  const sortedAlerts = useMemo(
    () => sortAlertsBySeverityAndTime(incident.alerts),
    [incident.alerts]
  )

  const { activeAlerts, mutedAlerts } = useMemo(() => {
    const { active, muted } = partitionMutedAlerts(sortedAlerts)
    return { activeAlerts: active, mutedAlerts: muted }
  }, [sortedAlerts])

  const severityLabel = t(`pages:monitoring.alerts.severity.${incident.maxSeverity}`, incident.maxSeverity)

  const RIBBON_BG: Record<AlertSeverity, string> = {
    critical: 'bg-rose-500',
    warning: 'bg-amber-500',
    info: 'bg-sky-500',
  }

  return (
    <Card className="relative overflow-hidden">
      <span
        aria-hidden="true"
        className={cn('absolute left-0 top-0 h-full w-[3px]', RIBBON_BG[incident.maxSeverity])}
      />

      <div className="p-3 pl-4">
        <div className="flex items-center gap-2 mb-2">
          <Server size={16} className="text-slate-400" aria-hidden="true" />
          <Link
            to={`/hosts/${incident.hostId}`}
            className="font-medium text-slate-900 dark:text-slate-100 hover:underline"
          >
            {incident.hostName}
          </Link>
          <Text size="xs" color="muted" className="ml-1">
            {incident.environmentName}
          </Text>
          <div className="ml-auto flex items-center gap-2">
            <SeverityBadge
              severity={incident.maxSeverity}
              label={`${incident.alerts.length}`}
              ariaLabel={t('pages:monitoring.alerts.severityCount', '{{count}} {{severity}} alerts', {
                count: incident.alerts.length,
                severity: severityLabel,
              })}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          {activeAlerts.map(alert => (
            <AlertRowCompact
              key={alert.fingerprint}
              alert={alert}
              isSelected={selectedAlertId === alert.fingerprint}
              onSelect={() => onSelectAlert(alert.fingerprint)}
              onAck={() => onAck(alert.fingerprint)}
              onUnack={() => onUnack(alert.fingerprint)}
              onMute={(duration) => onMute(alert.fingerprint, duration)}
              onUnmute={() => onUnmute(alert.fingerprint)}
              isMutating={isMutating}
            />
          ))}

          {mutedAlerts.length > 0 && (
            <Collapsible open={mutedExpanded} onOpenChange={setMutedExpanded}>
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'w-full flex items-center justify-start gap-2 px-2 py-1.5 rounded text-sm font-normal',
                    'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800',
                    'transition-colors'
                  )}
                >
                  {mutedExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <BellOff size={14} />
                  <span>
                    {t('pages:monitoring.alerts.mutedCount', '{{count}} muted', { count: mutedAlerts.length })}
                  </span>
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-1.5 space-y-1.5 opacity-60">
                  {mutedAlerts.map(alert => (
                    <AlertRowCompact
                      key={alert.fingerprint}
                      alert={alert}
                      isSelected={selectedAlertId === alert.fingerprint}
                      onSelect={() => onSelectAlert(alert.fingerprint)}
                      onAck={() => onAck(alert.fingerprint)}
                      onUnack={() => onUnack(alert.fingerprint)}
                      onMute={(duration) => onMute(alert.fingerprint, duration)}
                      onUnmute={() => onUnmute(alert.fingerprint)}
                      isMutating={isMutating}
                    />
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      </div>
    </Card>
  )
}
